import { ChangeEventHandler, useState } from "react";
import { useTranslation } from "react-i18next";
import produce from "immer";
import { Box, TextField, Typography } from "@material-ui/core";
import SwitchField from "components/Forms/SwitchField";
import AutocompleteSearchInput from "components/AutocompleteSearchInput";
import { useRuleContext } from "pages/Rules/Form/context/ruleContext";

import { Field, FormFields } from "../../../../common/FormFields";
import { ErrorMessage } from "../../Actions/Styled";

import { DoneNotification } from "@dashboard-v3/api";
import { NOTIFICATION_LIMITS } from "@dashboard-v3/shared";
import useNotificationVariables from "pages/Rules/hooks/useNotificationVariables";
import { getApprovalSteps } from "pages/Rules/Form/context/ruleSelectors";

export default function DoneNotificationSettings() {
  const { variables, replaceIdsWithLabels, replaceLabelsWithIds } =
    useNotificationVariables({
      ruleType: "REQUEST_APPROVAL",
      notificationType: "DONE",
    });
  const { t } = useTranslation("rules");
  const { state, dispatch } = useRuleContext();
  const { doneNotification } = getApprovalSteps(state.rule);
  const [isEnabled, setIsEnabled] = useState(Boolean(doneNotification));
  const titleError = hasError(
    "COMPLETION_TITLE_REQUIRED",
    "COMPLETION_TITLE_MAX_LENGTH"
  );
  const approvedBodyError = hasError(
    "COMPLETION_APPROVEDBODYTEXT_REQUIRED",
    "COMPLETION_APPROVEDBODYTEXT_MAX_LENGTH"
  );
  const rejectedBodyError = hasError(
    "COMPLETION_REJECTEDBODYTEXT_REQUIRED",
    "COMPLETION_REJECTEDBODYTEXT_MAX_LENGTH"
  );

  const updateSettings = (update: Partial<DoneNotification>) => {
    dispatch({
      type: "updateStep",
      payload: {
        step: "approvalSteps",
        changes: { doneNotification: update },
      },
    });
  };

  const onChange: ChangeEventHandler<HTMLInputElement> = e => {
    const { name, value } = e.target;

    const update = produce(doneNotification, draft => {
      draft[name] = value;
    });

    updateSettings(update);
  };

  function onBodyChange(name: string, value: string) {
    const update = produce(doneNotification, draft => {
      draft[name] = replaceLabelsWithIds(value);
    });

    updateSettings(update);
  }

  const onEnableNotification: ChangeEventHandler<HTMLInputElement> = e => {
    const { checked } = e.target;
    setIsEnabled(checked);

    if (!checked) return updateSettings(null);

    updateSettings({
      title: t("approvalSteps.doneEmail.title"),
      approvedBodyText: t("approvalSteps.doneEmail.approvedBodyText"),
      rejectedBodyText: t("approvalSteps.doneEmail.rejectedBodyText"),
    });
  };

  function hasError(...errorCodes: string[]) {
    const errors = (state.steps.approvalSteps?.error || []) as string[];

    return errors.some(error => {
      const match = errorCodes.find(code => code === error);
      return Boolean(match);
    });
  }

  return (
    <Box mt="15px" mb="10px">
      <SwitchField
        style={{ marginBottom: isEnabled ? 10 : 0 }}
        label={t("approvalSteps.doneNotification.enableLabel")}
        description={
          !isEnabled && t("approvalSteps.doneNotification.enableDescription")
        }
        value={isEnabled}
        onChange={onEnableNotification}
      />
      {isEnabled && (
        <Box padding="8px 16px 16px">
          <FormFields>
            <Field>
              <TextField
                name="title"
                variant="outlined"
                label={t("approvalSteps.doneNotification.title")}
                value={doneNotification.title}
                onChange={onChange}
                error={titleError}
                inputProps={{ maxLength: NOTIFICATION_LIMITS.TITLE }}
              />
              <ErrorMessage
                isError={titleError}
                wording={t("approvalSteps.fieldRequired")}
              />
            </Field>

            <Field>
              <Typography
                variant="caption"
                color={approvedBodyError ? "error" : "textSecondary"}
              >
                {t("approvalSteps.doneNotification.approvalBody")}
              </Typography>
              <AutocompleteSearchInput
                name="approvedBodyText"
                multiple
                rows={5}
                fullWidth
                variables={variables}
                error={approvedBodyError}
                maxLength={NOTIFICATION_LIMITS.BODY_TEXT}
                value={replaceIdsWithLabels(doneNotification.approvedBodyText)}
                onChange={value => onBodyChange("approvedBodyText", value)}
              />
              <ErrorMessage
                isError={approvedBodyError}
                wording={t("approvalSteps.fieldRequired")}
              />
            </Field>

            <Field>
              <Typography
                variant="caption"
                color={rejectedBodyError ? "error" : "textSecondary"}
              >
                {t("approvalSteps.doneNotification.rejectBody")}
              </Typography>
              <AutocompleteSearchInput
                name="rejectedBodyText"
                multiple
                rows={5}
                fullWidth
                variables={variables}
                error={rejectedBodyError}
                maxLength={NOTIFICATION_LIMITS.BODY_TEXT}
                value={replaceIdsWithLabels(doneNotification.rejectedBodyText)}
                onChange={value => onBodyChange("rejectedBodyText", value)}
              />
              <ErrorMessage
                isError={rejectedBodyError}
                wording={t("approvalSteps.fieldRequired")}
              />
            </Field>
          </FormFields>
        </Box>
      )}
    </Box>
  );
}
